import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import AdminLayout from './components/layout/AdminLayout'
import CandidateLayout from './components/layout/CandidateLayout'
import HeadhunterLayout from './components/layout/HeadhunterLayout'
import HrLayout from './components/layout/HrLayout'

// Auth
import Login from './pages/Login'
import Register from './pages/Register'
import AuthCallback from './pages/AuthCallback'

// Core pages
import HiringHarder from './pages/HiringHarder'

// Admin pages
import Dashboard from './pages/Dashboard'
import Applicants from './pages/Applicants'
import ApplicantDetail from './pages/ApplicantDetail'
import Jobs from './pages/Jobs'
import JobDetail from './pages/JobDetail'
import MatchReports from './pages/MatchReports'
import MatchReportDetail from './pages/MatchReportDetail'

// Candidate pages
import CandidateDashboard from './pages/candidate/CandidateDashboard'
import CandidateJobs from './pages/candidate/CandidateJobs'
import CandidateProfileDetail from './pages/candidate/CandidateProfileDetail'

// Headhunter pages
import HeadhunterDashboard from './pages/headhunter/HeadhunterDashboard'
import HeadhunterJobs from './pages/headhunter/HeadhunterJobs'
import HeadhunterJobDetail from './pages/headhunter/HeadhunterJobDetail'
import HeadhunterCandidates from './pages/headhunter/HeadhunterCandidates'
import HeadhunterCandidateDetail from './pages/headhunter/HeadhunterCandidateDetail'
import HeadhunterPipeline from './pages/headhunter/HeadhunterPipeline'

// HR pages
import HrDashboard from './pages/hr/HrDashboard'
import HrJobs from './pages/hr/HrJobs'
import HrJobDetail from './pages/hr/HrJobDetail'
import HrReports from './pages/hr/HrReports'
import HrCandidateDetail from './pages/hr/HrCandidateDetail'

function RoleRedirect() {
  const { user, isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-sm text-text-muted">Loading...</p>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const role = user?.role || 'viewer'
  if (role === 'admin') return <Navigate to="/admin" replace />
  if (role === 'editor') return <Navigate to="/headhunter" replace />
  return <Navigate to="/candidate" replace />
}

function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { user, isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-sm text-text-muted">Loading...</p>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/hiring-harder" element={<HiringHarder />} />

          {/* Root redirect based on role */}
          <Route path="/" element={<RoleRedirect />} />

          {/* Admin portal — admin only */}
          <Route path="/admin/*" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminRoutes />
            </ProtectedRoute>
          } />

          {/* Headhunter portal — admin + editor */}
          <Route path="/headhunter/*" element={
            <ProtectedRoute allowedRoles={['admin', 'editor']}>
              <HeadhunterRoutes />
            </ProtectedRoute>
          } />

          {/* HR portal — admin + editor (for now, same as headhunter) */}
          <Route path="/hr/*" element={
            <ProtectedRoute allowedRoles={['admin', 'editor']}>
              <HrRoutes />
            </ProtectedRoute>
          } />

          {/* Candidate portal — all authenticated */}
          <Route path="/candidate/*" element={
            <ProtectedRoute>
              <CandidateRoutes />
            </ProtectedRoute>
          } />

          {/* Legacy redirects */}
          <Route path="/candidates" element={<Navigate to="/admin/applicants" replace />} />
          <Route path="/applicants" element={<Navigate to="/admin/applicants" replace />} />
          <Route path="/jobs" element={<Navigate to="/admin/jobs" replace />} />
          <Route path="/match-reports" element={<Navigate to="/admin/match-reports" replace />} />
          <Route path="/match-reports/:jobId" element={<Navigate to="/admin/match-reports" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}

function AdminRoutes() {
  return (
    <AdminLayout>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="applicants" element={<Applicants />} />
        <Route path="applicants/:id" element={<ApplicantDetail />} />
        <Route path="jobs" element={<Jobs />} />
        <Route path="jobs/:id" element={<JobDetail />} />
        <Route path="match-reports" element={<MatchReports />} />
        <Route path="match-reports/:jobId" element={<MatchReportDetail />} />
      </Routes>
    </AdminLayout>
  )
}

function HeadhunterRoutes() {
  return (
    <HeadhunterLayout>
      <Routes>
        <Route index element={<HeadhunterDashboard />} />
        <Route path="jobs" element={<HeadhunterJobs />} />
        <Route path="jobs/:id" element={<HeadhunterJobDetail />} />
        <Route path="candidates" element={<HeadhunterCandidates />} />
        <Route path="candidates/:id" element={<HeadhunterCandidateDetail />} />
        <Route path="pipeline" element={<HeadhunterPipeline />} />
      </Routes>
    </HeadhunterLayout>
  )
}

function HrRoutes() {
  return (
    <HrLayout>
      <Routes>
        <Route index element={<HrDashboard />} />
        <Route path="jobs" element={<HrJobs />} />
        <Route path="jobs/:id" element={<HrJobDetail />} />
        <Route path="reports" element={<HrReports />} />
        <Route path="candidates/:id" element={<HrCandidateDetail />} />
      </Routes>
    </HrLayout>
  )
}

function CandidateRoutes() {
  return (
    <CandidateLayout>
      <Routes>
        <Route index element={<CandidateDashboard />} />
        <Route path="jobs" element={<CandidateJobs />} />
        <Route path="profile/:id" element={<CandidateProfileDetail />} />
      </Routes>
    </CandidateLayout>
  )
}
